import { memo, useEffect, useRef, useState, type DragEvent, type FormEvent, type KeyboardEvent, type MouseEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { BiblePassage, BibleTranslation } from '../../shared/api/types';
import { Alert, Button } from '../../shared/ui/components';
import { DRAG_TYPE, type NewItem } from './ConsoleLibrary';
import { BibleRefInput } from './BibleRefInput';

/** Proyecta un versículo: el capítulo queda en Programa (una sola vez) y se pone al aire esa diapositiva. */
export type ProjectScripture = (chapter: string, translation: string | null, slide: number) => Promise<void>;

const VERSION_KEY = 'keora.consola.version';

/** «Éxodo 3:12» → «Éxodo 3». */
const chapterOf = (label: string) => label.replace(/:.*$/, '');

/**
 * Palabra, como en EasyWorship: versiones a la izquierda; se escribe la cita y sale todo el
 * capítulo en lista (versión, cita y texto). Un clic pone ese versículo al aire y las flechas ↑ ↓
 * avanzan versículo por versículo; Shift+clic marca un rango. Lo marcado se arrastra a Programa.
 */
export const WordTab = memo(function WordTab({ onAdd, onProject }: { onAdd: (item: NewItem) => Promise<void>; onProject: ProjectScripture }) {
  const [translations, setTranslations] = useState<BibleTranslation[]>([]);
  const [version, setVersion] = useState<string | null>(() => {
    try {
      return localStorage.getItem(VERSION_KEY);
    } catch {
      return null;
    }
  });
  const [query, setQuery] = useState<string | null>(null);
  const [chapter, setChapter] = useState<BiblePassage | null>(null);
  // Versículo elegido (índice en el capítulo) y el otro extremo del rango (Shift+clic).
  const [current, setCurrent] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void api
      .get<{ items: BibleTranslation[] }>('/bible/translations')
      .then((r) => setTranslations(r.items.filter((t) => t.language === 'es')))
      .catch(() => undefined);
  }, []);

  const code = version && translations.some((t) => t.code === version) ? version : (translations[0]?.code ?? null);

  async function load(ref: string, verseCode: string | null) {
    setLoading(true);
    setError(null);
    try {
      const q = (r: string) => `/bible/passage?ref=${encodeURIComponent(r)}${verseCode ? `&translation=${verseCode}` : ''}`;
      const target = await api.get<BiblePassage>(q(ref));
      // El capítulo, en la misma versión del versículo (aunque todavía no se supiera cuál era).
      const used = verseCode ?? target.translation?.code ?? null;
      const whole = await api.get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(chapterOf(target.label))}${used ? `&translation=${used}` : ''}`);
      setChapter(whole);
      const idx = Math.max(0, whole.verses.findIndex((v) => v.verse === (target.reference.verseStart ?? whole.verses[0]?.verse)));
      setCurrent(idx);
      setAnchor(null);
      requestAnimationFrame(() => listRef.current?.querySelector<HTMLElement>(`[data-i="${idx}"]`)?.scrollIntoView?.({ block: 'center' }));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (query) void load(query, code);
  }

  function pickVersion(c: string) {
    setVersion(c);
    try {
      localStorage.setItem(VERSION_KEY, c);
    } catch {
      // Sin almacenamiento: solo no se recuerda.
    }
    if (chapter) void load(current !== null && chapter.verses[current] ? `${chapterOf(chapter.label)}:${chapter.verses[current]!.verse}` : chapter.label, c);
  }

  const verses = chapter?.verses ?? [];
  const label = chapter ? chapterOf(chapter.label) : '';
  const range: [number, number] | null = current === null ? null : anchor === null ? [current, current] : [Math.min(anchor, current), Math.max(anchor, current)];
  const inRange = (i: number) => range !== null && i >= range[0] && i <= range[1];
  const refOf = (a: number, b: number) => (a === b ? `${label}:${verses[a]!.verse}` : `${label}:${verses[a]!.verse}-${verses[b]!.verse}`);
  const rangeItem = (): NewItem | null => (range && verses[range[0]] ? { kind: 'scripture', title: `${refOf(range[0], range[1])}${code ? ` (${code})` : ''}`.slice(0, 120), passage: refOf(range[0], range[1]), translation: code } : null);

  async function project(i: number) {
    if (!chapter || !verses[i]) return;
    setError(null);
    setDone(null);
    try {
      await onProject(label, code, i);
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  function click(e: MouseEvent, i: number) {
    if (e.shiftKey && current !== null) {
      setAnchor(anchor ?? current);
      setCurrent(i);
      return;
    }
    setAnchor(null);
    setCurrent(i);
    void project(i);
  }

  function keys(e: KeyboardEvent<HTMLDivElement>) {
    if (current === null || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    e.preventDefault();
    const next = Math.max(0, Math.min(verses.length - 1, current + (e.key === 'ArrowDown' ? 1 : -1)));
    if (e.shiftKey) {
      setAnchor(anchor ?? current);
      setCurrent(next);
    } else {
      setAnchor(null);
      setCurrent(next);
      if (next !== current) void project(next);
    }
    listRef.current?.querySelector<HTMLElement>(`[data-i="${next}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }

  function dragStart(e: DragEvent, i: number) {
    // Arrastrar fuera del rango marcado arrastra solo ese versículo.
    const item = inRange(i) ? rangeItem() : { kind: 'scripture' as const, title: `${refOf(i, i)}${code ? ` (${code})` : ''}`, passage: refOf(i, i), translation: code };
    if (!item) return;
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(item));
    e.dataTransfer.setData('text/plain', item.title);
    e.dataTransfer.effectAllowed = 'copy';
  }

  async function addRange() {
    const item = rangeItem();
    if (!item) return;
    setError(null);
    try {
      await onAdd(item);
      setDone(`«${item.title}» está en Programa.`);
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  const shown = current !== null ? verses[current] : null;

  return (
    <div className="ewc__bible">
      <nav className="ewc__side" aria-label="Versión">
        {translations.map((t) => (
          <button key={t.code} type="button" className={`ewc__side-item${code === t.code ? ' ewc__side-item--active' : ''}`} aria-pressed={code === t.code} title={t.name} onClick={() => pickVersion(t.code)}>
            {t.code}
          </button>
        ))}
      </nav>
      <div className="ewc__listpane">
        <form className="ewc__search" onSubmit={submit}>
          <label className="visually-hidden" htmlFor="ewc-passage">
            Buscar en la Palabra
          </label>
          <BibleRefInput id="ewc-passage" onQuery={setQuery} onSearch={(ref) => void load(ref, code)} />
          <Button type="submit" variant="secondary" busy={loading} disabled={!query}>
            Buscar
          </Button>
          {range && range[1] > range[0] ? (
            <Button type="button" onClick={() => void addRange()}>
              {`${refOf(range[0], range[1])} a Programa`}
            </Button>
          ) : null}
        </form>
        {error ? <Alert>{error}</Alert> : null}
        {done ? (
          <p className="small ewc__done" role="status">
            {done}
          </p>
        ) : null}
        {chapter ? (
          <div className="ewc__verses" ref={listRef} tabIndex={0} role="listbox" aria-label={`${label} (${code ?? ''})`} aria-multiselectable onKeyDown={keys}>
            <div className="ewc__verse ewc__verse--head" aria-hidden="true">
              <span>Versión</span>
              <span>Cita</span>
              <span>Texto</span>
            </div>
            {verses.map((v, i) => (
              <div
                key={v.verse}
                data-i={i}
                role="option"
                aria-selected={inRange(i)}
                className={`ewc__verse${inRange(i) ? ' ewc__verse--selected' : ''}${i === current ? ' ewc__verse--current' : ''}`}
                draggable
                onDragStart={(e) => dragStart(e, i)}
                onClick={(e) => click(e, i)}
              >
                <span>{chapter.translation?.code ?? ''}</span>
                <span>
                  {label}:{v.verse}
                </span>
                <span className="ewc__verse-text">{v.text}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="small muted">Escribe la cita y presiona Enter. Clic en un versículo: se muestra · ↑ ↓: el anterior o el siguiente · Shift+clic: marcar varios · Arrástralos a Programa para guardarlos en el culto.</p>
        )}
      </div>
      <div className="ewc__bible-preview" aria-label="Vista previa">
        {shown ? (
          <div className="slide ewc__bible-slide">
            <p className="slide__text slide__text--small">
              <sup>{shown.verse}</sup> {shown.text}
            </p>
            <p className="slide__caption">
              {label}:{shown.verse}
              {code ? ` (${code})` : ''}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
});
